import { z } from 'zod';

// ── Marketplace event payloads ───────────────────────────────────────
// Minimal ids only — the worker rehydrates the full order snapshot from the DB.

export const OrderSentPayloadSchema = z.object({
    orderId: z.string().cuid2(),
    tenantId: z.string().cuid2(),
    vendorId: z.string().cuid2(),
    /** Tenant-facing app origin for per-script partner-view CTAs. */
    appBaseUrl: z.string().url().optional(),
});

export type OrderSentPayload = z.infer<typeof OrderSentPayloadSchema>;

export const VendorConnectedPayloadSchema = z.object({
    connectionId: z.string().cuid2(),
    tenantId: z.string().cuid2(),
    vendorId: z.string().cuid2(),
});

export type VendorConnectedPayload = z.infer<typeof VendorConnectedPayloadSchema>;

/**
 * A vendor's published T&C were edited, so every tenant signed at
 * `previousTermsVersion` is now blocked from sending until they re-sign. The
 * worker resolves the affected connections — the set is only knowable at
 * delivery time (AD-2298).
 */
export const VendorTermsUpdatedPayloadSchema = z.object({
    vendorId: z.string().cuid2(),
    termsVersion: z.number().int().positive(),
    previousTermsVersion: z.number().int().nonnegative(),
});

export type VendorTermsUpdatedPayload = z.infer<typeof VendorTermsUpdatedPayloadSchema>;

export const ScriptPreflightSubmittedPayloadSchema = z.object({
    orderId: z.string().cuid2(),
    orderScriptId: z.string().cuid2(),
    vendorId: z.string().cuid2(),
    preflightUrl: z.string().url().optional(),
    scriptReviewId: z.string().cuid2().optional(),
    submissionId: z.string().min(1).optional(),
    attemptVersion: z.string().min(1).optional(),
});

export type ScriptPreflightSubmittedPayload = z.infer<
    typeof ScriptPreflightSubmittedPayloadSchema
>;

export const ScriptRevisionRequestedPayloadSchema = z.object({
    orderId: z.string().cuid2(),
    orderScriptId: z.string().cuid2(),
    tenantId: z.string().cuid2(),
    revisionNote: z.string(),
    /** When set, revision applies to one video instead of the whole script line. */
    deliverableId: z.string().cuid2().optional(),
    /** Tenant-facing app origin for the partner production tracker CTA. */
    appBaseUrl: z.string().url().optional(),
});

export type ScriptRevisionRequestedPayload = z.infer<
    typeof ScriptRevisionRequestedPayloadSchema
>;

export const ScriptCommentAddedPayloadSchema = z.object({
    orderId: z.string().cuid2(),
    orderScriptId: z.string().cuid2(),
    tenantId: z.string().cuid2(),
    commentId: z.string().cuid2(),
    commentBody: z.string(),
    authorUserId: z.string().cuid2(),
    /** Who posted the comment — drives attribution in the email (not the order client). */
    commentAuthorRole: z.enum(["admin", "client", "partner"]),
    /** Tenant-facing app origin for the partner production tracker CTA. */
    appBaseUrl: z.string().url().optional(),
});

export type ScriptCommentAddedPayload = z.infer<
    typeof ScriptCommentAddedPayloadSchema
>;

export const ScriptSubmittedForFinalApprovalPayloadSchema = z.object({
    orderId: z.string().cuid2(),
    orderScriptId: z.string().cuid2(),
    tenantId: z.string().cuid2(),
    scriptReviewId: z.string().cuid2(),
    /** Tenant-facing app origin for the preflight review CTA. */
    appBaseUrl: z.string().url().optional(),
    /**
     * The line's `publishAttemptId` for this transition. The worker claims
     * that exact attempt before sending, so an unmark that already cancelled
     * it does not email the client. Optional for events published before the
     * field existed.
     */
    attemptVersion: z.string().min(1).optional(),
});

export type ScriptSubmittedForFinalApprovalPayload = z.infer<
    typeof ScriptSubmittedForFinalApprovalPayloadSchema
>;
